import type { Ref } from "react";
import classNames from "classnames";
import {
    circleTilePath,
    roundedMarkPath,
    squareMarkPath,
    squareTilePath,
    wordmarkPath,
} from "@common/constants/kickLogoPaths";
import styles from "./KickLogo.module.scss";

// Visual source of truth: docs/specs/2026-09-30-kick-logo.md.

/** xs 16px, sm 20px, md 24px, lg 32px, xl 40px, 2xl 48px (the height on `full`). */
export type KickLogoSize = "xs" | "sm" | "md" | "lg" | "xl" | "2xl";

/** Tile colour: black paints the tile `--fg-primary`, white `--fg-on-solid`. */
export type KickLogoColor = "black" | "white";

export type KickLogoShape = "square" | "circle";

type KickLogoVariantProps =
    | { variant?: "full"; shape?: never }
    | { variant: "icon"; shape?: KickLogoShape };

export type KickLogoVariant = NonNullable<KickLogoVariantProps["variant"]>;

export type KickLogoProps = KickLogoVariantProps & {
    color?: KickLogoColor;
    /** Cuts the mark out of the tile so the surface shows through. */
    transparent?: boolean;
    size?: KickLogoSize;
    /** Exposes the logo as a labelled image. Omit when it is decorative. */
    label?: string;
    className?: string;
    ref?: Ref<SVGSVGElement>;
};

const tilePaths: Record<KickLogoShape, string> = {
    square: squareTilePath,
    circle: circleTilePath,
};

const markPaths: Record<KickLogoShape, string> = {
    square: squareMarkPath,
    circle: roundedMarkPath,
};

const LogoTile = ({
    shape,
    isTransparent,
}: {
    shape: KickLogoShape;
    isTransparent: boolean;
}) =>
    isTransparent ? (
        <path
            className={classNames(styles.tile, styles.cutout)}
            d={`${tilePaths[shape]}${markPaths[shape]}`}
        />
    ) : (
        <>
            <path className={styles.tile} d={tilePaths[shape]} />
            <path className={styles.mark} d={markPaths[shape]} />
        </>
    );

/**
 * The Kick logo from the Figma KickLogo (`full`: tile plus "kick") and
 * KickIcon (`icon`: tile only, square or circle) sets, drawn as one inline
 * SVG. Paint comes from CSS, so Button, Tag and MenuItem icon slots never
 * recolor it. As an `Avatar` fallback, `<KickLogo variant="icon" />` fills
 * the avatar box on its own.
 *
 * @example <KickLogo variant="icon" size="lg" label="Kick" />
 */
export const KickLogo = ({
    variant = "full",
    shape,
    color = "black",
    transparent = false,
    size = "2xl",
    label,
    className,
    ref,
}: Readonly<KickLogoProps>) => {
    const isIcon = variant === "icon";
    const tileShape = isIcon ? (shape ?? "square") : "square";
    const hasLabel = label !== undefined && label !== "";
    const accessibilityProps = hasLabel
        ? { role: "img", "aria-label": label }
        : { "aria-hidden": true };

    return (
        <svg
            ref={ref}
            viewBox={isIcon ? "0 0 48 48" : "0 0 150 48"}
            className={classNames(styles.kickLogo, className)}
            data-variant={variant}
            data-shape={isIcon ? tileShape : undefined}
            data-color={color}
            data-size={size}
            data-transparent={transparent || undefined}
            {...accessibilityProps}
        >
            <LogoTile shape={tileShape} isTransparent={transparent} />
            {!isIcon && <path className={styles.wordmark} d={wordmarkPath} />}
        </svg>
    );
};
